"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import MonthlyLimitDisplay from "@/components/MonthlyLimitDisplay";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronUp,
  LogIn,
  Info,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MapPin,
  Clock,
  Sparkles,
  FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useSession } from "next-auth/react";
import { useDebounce } from 'use-debounce';

interface TripFormData {
  destination: string;
  city: string;  // Added separate city field for geolocation features
  startDate: Date | undefined;
  endDate: Date | undefined;
  flightDetails?: string;
  preferences?: string;
  mustVisitPlaces?: string;
  budget?: string;
  travelStyle?: string;
}

interface TripFormProps {
  onSubmit: (data: TripFormData, useAI: boolean) => void;
  isLoading: boolean;
  initialData?: TripFormData;
}

interface Limits {
  remainingMonthly: number;
  remainingStorage: number | "Unlimited";
  planTier: string;
}

const TRAVEL_STYLES = [
  { value: "leisure", label: "Leisure & Relaxation" },
  { value: "adventure", label: "Adventure & Outdoor" },
  { value: "cultural", label: "Cultural & Historical" },
  { value: "foodie", label: "Food & Culinary" },
  { value: "luxury", label: "Luxury & Premium" },
  { value: "budget", label: "Budget Friendly" },
  { value: "family", label: "Family Oriented" },
  { value: "solo", label: "Solo Travel" },
];

export default function TripForm({
  onSubmit,
  isLoading,
  initialData,
}: TripFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TripFormData>({
    defaultValues: initialData,
  });
  const [startDate, setStartDate] = useState<Date | undefined>(
    initialData?.startDate ? new Date(initialData.startDate) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    initialData?.endDate ? new Date(initialData.endDate) : undefined
  );
  const [isOpen, setIsOpen] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [limits, setLimits] = useState<Limits | null>(null);
  const [cityInput, setCityInput] = useState(initialData?.city || '');
  const [suggestions, setSuggestions] = useState<Array<{display: string; value: string; latitude: number | null; longitude: number | null}>>([]);
  const [validCities, setValidCities] = useState<string[]>([]);
  const [isCityValid, setIsCityValid] = useState(!!initialData?.city);
  const [isCityFocused, setIsCityFocused] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [useAI, setUseAI] = useState(true); // Toggle for AI vs Template
  const [debouncedCityInput] = useDebounce(cityInput, 300);
  const { data: session, status } = useSession();
  
  // Date validation helper
  const areDatesValid = useCallback(() => {
    if (!startDate || !endDate) return true;
    return endDate >= startDate;
  }, [startDate, endDate]);
  
  // Form completion tracking
  const formCompletion = useMemo(() => {
    const datesValid = areDatesValid();
    const requiredFields = {
      destination: !!watch("destination"),
      city: isCityValid,
      startDate: !!startDate,
      endDate: !!endDate && datesValid,
    };
    const completed = Object.values(requiredFields).filter(Boolean).length;
    const total = Object.keys(requiredFields).length;
    return { completed, total, percentage: (completed / total) * 100 };
  }, [watch("destination"), isCityValid, startDate, endDate, areDatesValid]);
  
  // Fetch city suggestions when the debounced input changes
  useEffect(() => {
    const fetchCitySuggestions = async () => {
      if (debouncedCityInput.length < 2) {
        setSuggestions([]);
        setCityLoading(false);
        return;
      }
      
      setCityLoading(true);
      try {
        const response = await fetch(`/api/cities/suggestions?query=${encodeURIComponent(debouncedCityInput)}`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
          setValidCities(data.validCities || []);
        }
      } catch (error) {
        console.error("Error fetching city suggestions:", error);
        setSuggestions([]);
      } finally {
        setCityLoading(false);
      }
    };
    
    fetchCitySuggestions();
  }, [debouncedCityInput]);
  
  // Validate city when input changes
  useEffect(() => {
    // Check if the current input matches any valid city
    if (cityInput) {
      const isValid = validCities.some(city => 
        city.toLowerCase() === cityInput.toLowerCase()
      );
      setIsCityValid(isValid);
    } else {
      setIsCityValid(false);
    }
  }, [cityInput, validCities]);
  
  // Function to handle city input changes
  const handleCityInputChange = (value: string) => {
    setCityInput(value);
    setValue("city", value); // Update form value
  };

  const getDateError = () => {
    if (!areDatesValid()) {
      return "End date must be after start date";
    }
    return null;
  };

  const tripDays =
    startDate && endDate
      ? Math.ceil(
          (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
        ) + 1
      : 0;

  // Quick date presets
  const getQuickDatePresets = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const nextWeekEnd = new Date(nextWeek);
    nextWeekEnd.setDate(nextWeekEnd.getDate() + 2); // Weekend trip
    const nextMonthEnd = new Date(nextMonth);
    nextMonthEnd.setDate(nextMonthEnd.getDate() + 6); // Week-long trip

    return [
      { label: "Tomorrow", start: tomorrow, end: new Date(tomorrow.getTime() + 2 * 24 * 60 * 60 * 1000) },
      { label: "Next Weekend", start: nextWeekEnd, end: new Date(nextWeekEnd.getTime() + 1 * 24 * 60 * 60 * 1000) },
      { label: "Next Week", start: nextWeek, end: new Date(nextWeek.getTime() + 6 * 24 * 60 * 60 * 1000) },
      { label: "Next Month", start: nextMonth, end: new Date(nextMonth.getTime() + 6 * 24 * 60 * 60 * 1000) },
    ];
  };

  const fetchLimits = useCallback(async () => {
    try {
      // Only fetch if user is authenticated
      if (status === "authenticated") {
        const response = await fetch("/api/user/limits", {
          // Add cache headers
          headers: {
            'Cache-Control': 'max-age=300', // Cache for 5 minutes
          }
        });
        if (response.ok) {
          const data = await response.json();
          setLimits(data);
        }
      }
    } catch (error) {
      console.error("Error fetching limits:", error);
    }
  }, [status]);

  // Only fetch limits when session status changes to authenticated
  useEffect(() => {
    if (status === "authenticated") {
      fetchLimits();
    }
  }, [status, fetchLimits]);

  const onFormSubmit = handleSubmit((data) => {
    if (!startDate || !endDate) {
      return;
    }
    
    // Additional city validation before submission
    if (!isCityValid) {
      setValue("city", data.city, { 
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
      return;
    }
    
    onSubmit({ ...data, startDate, endDate }, useAI);
  });

  const handleStartDateSelect = (date: Date | undefined) => {
    setStartDate(date);
    setStartDateOpen(false);
  };

  const handleEndDateSelect = (date: Date | undefined) => {
    setEndDate(date);
    setEndDateOpen(false);
  };

  const hasEnoughLimit =
    limits?.remainingMonthly && limits.remainingMonthly >= tripDays;
  const isFormDisabled =
    isLoading || !areDatesValid() || (useAI && !hasEnoughLimit) || tripDays < 1 || !isCityValid;

  return (
    <form
      onSubmit={onFormSubmit}
      className="space-y-4 w-full"
    >

      {/* Required Fields Section */}
      <div className="space-y-4 bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm">
        {/* Compact Form Progress Indicator */}
        {formCompletion.completed > 0 && formCompletion.completed < formCompletion.total && (
          <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
            <div className="flex-1">
              <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${formCompletion.percentage}%` }}
                />
              </div>
            </div>
            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
              {formCompletion.completed}/{formCompletion.total}
            </span>
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-800">
            Trip Name <span className="text-red-500">*</span>
          </label>
          <Input
            {...register("destination", { required: true })}
            placeholder="e.g., Summer Holiday 2025"
            className="h-11 bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
          />
          {errors.destination && (
            <span className="text-xs text-red-600 font-medium flex items-center gap-1">
              Trip name is required
            </span>
          )}
        </div>

        <div className="space-y-2 relative">
          <label className="text-sm font-semibold text-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <span className="flex items-center gap-1.5">
              City <span className="text-red-500">*</span>
              <span className="text-xs font-normal text-gray-500">(for attractions and weather)</span>
            </span>
            {cityInput && (
              <span className={`text-xs font-medium flex items-center gap-1 ${isCityValid ? 'text-green-600' : cityLoading ? 'text-blue-600' : 'text-amber-600'}`}>
                {cityLoading ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Searching...
                  </>
                ) : isCityValid ? (
                  <>
                    <CheckCircle2 className="w-3 h-3" />
                    Valid city
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-3 h-3" />
                    Select from suggestions
                  </>
                )}
              </span>
            )}
          </label>
          <div className="relative">
            <div className="relative">
              <Input
                {...register("city", { 
                  required: true,
                  validate: value => isCityValid || "Please select a valid city from the suggestions"
                })}
                placeholder="e.g., Tokyo, Paris, New York"
                className={`h-11 bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 transition-colors pr-10
                  ${cityInput && !isCityValid && !isCityFocused && !cityLoading ? 'border-amber-500 focus:border-amber-500 focus:ring-amber-500' : ''}
                  ${cityInput && isCityValid ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : ''}`}
                value={cityInput}
                onChange={(e) => {
                  handleCityInputChange(e.target.value);
                  if (e.target.value.length < 2) {
                    setSuggestions([]);
                  }
                }}
                onFocus={() => setIsCityFocused(true)}
                onBlur={() => setTimeout(() => setIsCityFocused(false), 200)}
                autoComplete="off"
              />
              {cityLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                </div>
              )}
              {!cityLoading && cityInput && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isCityValid ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                  )}
                </div>
              )}
            </div>
            {suggestions.length > 0 && isCityFocused && (
              <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-lg shadow-xl mt-1 max-h-48 overflow-y-auto">
                {suggestions.map((suggestion) => (
                  <div
                    key={suggestion.value}
                    className="px-4 py-2.5 cursor-pointer hover:bg-indigo-50 text-sm transition-colors border-b border-slate-100 last:border-b-0 flex items-center gap-2"
                    onClick={() => {
                      handleCityInputChange(suggestion.value);
                      setIsCityValid(true);
                      setSuggestions([]);
                    }}
                  >
                    <MapPin className="w-4 h-4 text-indigo-500" />
                    {suggestion.display}
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.city && (
            <span className="text-xs text-red-600 font-medium flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.city.message || "City is required for location-based features"}
            </span>
          )}
          {!errors.city && cityInput && !isCityValid && !cityLoading && (
            <p className="text-xs text-gray-500 flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>Type at least 2 characters to see city suggestions</span>
            </p>
          )}
        </div>

        <div className="space-y-4">

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                Start Date <span className="text-red-500">*</span>
                {startDate && <CheckCircle2 className="w-3 h-3 text-green-500" />}
              </label>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-11 justify-start text-left font-normal bg-white border-slate-300 hover:bg-slate-50 transition-colors",
                      !startDate && "text-gray-500",
                      startDate && "border-green-500"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    defaultMonth={startDate}
                    onSelect={handleStartDateSelect}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                End Date <span className="text-red-500">*</span>
                {endDate && areDatesValid() && <CheckCircle2 className="w-3 h-3 text-green-500" />}
              </label>
              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-11 justify-start text-left font-normal bg-white border-slate-300 hover:bg-slate-50 transition-colors",
                      !endDate && "text-gray-500",
                      endDate && areDatesValid() && "border-green-500",
                      endDate && !areDatesValid() && "border-red-500"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    defaultMonth={endDate || startDate}
                    onSelect={handleEndDateSelect}
                    disabled={(date) => startDate ? date < startDate : date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Trip Duration Display */}
          {startDate && endDate && areDatesValid() && (
            <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <Clock className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-900">
                Trip Duration: <span className="font-semibold">{tripDays} {tripDays === 1 ? 'day' : 'days'}</span>
                {tripDays > 14 && (
                  <span className="ml-2 text-xs text-amber-600">(Long trip - may take longer to generate)</span>
                )}
              </span>
            </div>
          )}

          {getDateError() && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600 font-medium">{getDateError()}</p>
            </div>
          )}
        </div>
      </div>

      {/* Optional Fields Collapsible */}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 flex items-center justify-between rounded-lg transition-colors"
          >
            <span className="text-gray-700 text-sm font-medium">Additional Details (Optional)</span>
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-4 mt-4 bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-800">
              Travel Style
            </label>
            <Select onValueChange={(value) => setValue("travelStyle", value)}>
              <SelectTrigger className="h-11 bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500">
                <SelectValue placeholder="Choose your travel style" />
              </SelectTrigger>
              <SelectContent>
                {TRAVEL_STYLES.map((style) => (
                  <SelectItem key={style.value} value={style.value}>
                    {style.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-800">
              Flight Details
            </label>
            <Input
              {...register("flightDetails")}
              placeholder="e.g., Arriving at 9AM on JAL"
              className="h-11 bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-800">
              Must-Visit Places
            </label>
            <Textarea
              {...register("mustVisitPlaces")}
              placeholder="List any specific places you'd like to visit"
              className="min-h-[100px] bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 resize-none"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-800">
              Travel Preferences
            </label>
            <Textarea
              {...register("preferences")}
              placeholder="e.g., Food preferences, accessibility needs, travel pace"
              className="min-h-[100px] bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 resize-none"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-800">
              Budget Range
            </label>
            <Input
              {...register("budget")}
              placeholder="e.g., $200-300 per day"
              className="h-11 bg-white border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Submit Section */}
      <div className="space-y-3">
        {/* AI Toggle with Credits and Plan Info */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          {/* Toggle Row */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {useAI ? (
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                ) : (
                  <FileText className="w-4 h-4 text-gray-600" />
                )}
                <span className="text-sm font-semibold text-gray-800">
                  {useAI ? "AI-Powered Plan" : "Blank Template"}
                </span>
              </div>
              <p className="text-xs text-gray-600">
                {useAI 
                  ? "Get a complete AI-generated itinerary with recommendations"
                  : "Start with an empty template (no credits required)"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setUseAI(!useAI)}
              className={cn(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex-shrink-0",
                useAI ? "bg-indigo-600" : "bg-gray-300"
              )}
              aria-label="Toggle AI mode"
            >
              <span
                className={cn(
                  "inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm",
                  useAI ? "translate-x-6" : "translate-x-1"
                )}
              />
            </button>
          </div>

          {/* Credits and Limits Info - Only show when AI is enabled */}
          {useAI && (
            <div className="pt-2">
                <MonthlyLimitDisplay requiredCredits={tripDays} />
              {tripDays > 7 && (
                <div className="text-xs text-gray-600 pt-2">
                  <p className="text-amber-600">💡 Tip: Longer trips may take a few minutes to generate</p>
                </div>
              )}
            </div>
          )}

          {/* Template Mode Info */}
          {!useAI && (
            <div className="pt-2">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <Info className="w-3.5 h-3.5 text-indigo-500" />
                <span>No credits required for blank templates</span>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          className={cn(
            "w-full h-12 font-semibold transition-all rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group",
            useAI
              ? "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
              : "bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
          )}
          disabled={isLoading || isFormDisabled}
        >
          {isLoading ? (
            <span className="flex items-center justify-center relative z-10">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              {useAI ? "Generating AI Trip Plan..." : "Creating Trip Template..."}
            </span>
          ) : (
            <span className="relative z-10 flex items-center justify-center gap-2">
              {useAI ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  Create AI Trip Plan
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Create Trip Template
                </>
              )}
              {formCompletion.completed === formCompletion.total && (
                <CheckCircle2 className="w-4 h-4" />
              )}
            </span>
          )}
          {!isFormDisabled && !isLoading && useAI && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
              animate={{
                x: ["100%", "-100%"],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          )}
        </Button>
      </div>
    </form>
  );
}
